import { useState } from "react";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plane, Plus, MapPin, Calendar as CalIcon, Users, Building2, Car, Trash2, Edit, Share2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShareDialog } from "@/components/ShareDialog";

const STATUS = ["planning", "confirmed", "completed"];
const Field = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-slate-700">{label}</Label>{children}</div>);

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

const TripDialog = ({ open, onOpenChange, initial }) => {
  const { addTrip, updateTrip } = useFgp();
  const editing = !!initial;
  const blank = { destination: "", purpose: "", startDate: "", endDate: "", travelers: "", status: "planning", flights: [], hotel: "", transfer: "", notes: "" };
  const [f, setF] = useState(initial || blank);
  const [seed, setSeed] = useState(initial?.id || "new");
  if (open && initial && initial.id !== seed) { setSeed(initial.id); setF({ ...blank, ...initial }); }
  if (open && !initial && seed !== "new") { setSeed("new"); setF(blank); }
  const flights = f.flights || [];
  const addFlight = () => setF({ ...f, flights: [...flights, { id: `fl_${Date.now()}`, label: "", detail: "" }] });
  const updateFlight = (id, patch) => setF({ ...f, flights: flights.map((x) => x.id === id ? { ...x, ...patch } : x) });
  const removeFlight = (id) => setF({ ...f, flights: flights.filter((x) => x.id !== id) });
  const submit = () => {
    if (!f.destination.trim()) return toast.error("Destination is required");
    if (editing) { updateTrip(initial.id, f); toast.success("Trip updated"); }
    else { addTrip(f); toast.success("Trip added"); }
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="trip-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit trip" : "Add trip"}</DialogTitle>
          <DialogDescription>Plan an itinerary for a principal or the family.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2 max-h-[62vh] overflow-y-auto pr-1">
          <Field label="Destination"><Input value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} data-testid="trip-destination-input" placeholder="e.g. Geneva, Switzerland" /></Field>
          <Field label="Purpose"><Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} placeholder="e.g. Board meeting" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date"><Input type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} /></Field>
            <Field label="End date"><Input type="date" value={f.endDate} onChange={(e) => setF({ ...f, endDate: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Travellers"><Input value={f.travelers} onChange={(e) => setF({ ...f, travelers: e.target.value })} placeholder="e.g. Jonathan Vance" /></Field>
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="trip-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          {/* Flights */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-slate-700 flex items-center gap-1"><Plane className="h-3.5 w-3.5" /> Flights</Label>
              <Button type="button" variant="outline" size="sm" onClick={addFlight} className="h-7 gap-1 text-xs" data-testid="trip-add-flight"><Plus className="h-3.5 w-3.5" /> Add flight</Button>
            </div>
            <div className="space-y-2">
              {flights.map((fl) => (
                <div key={fl.id} className="flex items-center gap-2" data-testid={`trip-flight-${fl.id}`}>
                  <Input value={fl.label} onChange={(e) => updateFlight(fl.id, { label: e.target.value })} placeholder="Route e.g. LCY → GVA" className="h-9 flex-1" data-testid="trip-flight-route" />
                  <Input value={fl.detail} onChange={(e) => updateFlight(fl.id, { detail: e.target.value })} placeholder="Airline / flight no / time" className="h-9 flex-[1.5]" data-testid="trip-flight-detail" />
                  <button type="button" onClick={() => removeFlight(fl.id)} className="text-slate-300 hover:text-rose-500 flex-shrink-0" data-testid={`trip-flight-del-${fl.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {flights.length === 0 && <p className="text-xs text-slate-400">No flights added yet.</p>}
            </div>
          </div>

          <Field label="Hotel / accommodation"><Input value={f.hotel} onChange={(e) => setF({ ...f, hotel: e.target.value })} /></Field>
          <Field label="Transfer / ground transport"><Input value={f.transfer} onChange={(e) => setF({ ...f, transfer: e.target.value })} /></Field>
          <Field label="Notes"><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="trip-save-btn">{editing ? "Save changes" : "Add trip"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TripDetail = ({ open, onOpenChange, trip, onEdit }) => {
  const [shareOpen, setShareOpen] = useState(false);
  if (!trip) return null;
  const flightsHtml = trip.flights?.length
    ? `<table><thead><tr><th>Route</th><th>Details</th></tr></thead><tbody>${trip.flights.map((fl) => `<tr><td>${fl.label || "—"}</td><td>${fl.detail || "—"}</td></tr>`).join("")}</tbody></table>`
    : "";
  const shareUrl = `${window.location.origin}/app/travel?t=${trip.id}`;
  const shareHtml = `
    <div class="card"><span class="k">Dates</span><div class="v">${fmt(trip.startDate)} → ${fmt(trip.endDate)}</div></div>
    <div class="card"><span class="k">Travellers</span><div class="v">${trip.travelers || "—"}</div></div>
    ${flightsHtml}
    <div class="card"><span class="k">Hotel / accommodation</span><div class="v">${trip.hotel || "—"}</div></div>
    <div class="card"><span class="k">Transfer / ground transport</span><div class="v">${trip.transfer || "—"}</div></div>
    ${trip.notes ? `<div class="card"><span class="k">Notes</span><div class="v">${trip.notes}</div></div>` : ""}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="trip-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-neutral-100 text-neutral-700 flex items-center justify-center"><Plane className="h-5 w-5" /></div>
            <div><DialogTitle className="font-heading">{trip.destination}</DialogTitle><DialogDescription>{trip.purpose}</DialogDescription></div>
            <span className="ml-auto"><StatusBadge status={trip.status} /></span>
          </div>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-200 p-3"><p className="text-xs text-slate-400 flex items-center gap-1"><CalIcon className="h-3 w-3" /> Dates</p><p className="text-sm font-medium text-slate-800 mt-0.5">{fmt(trip.startDate)} → {fmt(trip.endDate)}</p></div>
            <div className="rounded-lg border border-slate-200 p-3"><p className="text-xs text-slate-400 flex items-center gap-1"><Users className="h-3 w-3" /> Travellers</p><p className="text-sm font-medium text-slate-800 mt-0.5">{trip.travelers || "—"}</p></div>
          </div>
          {trip.flights?.length > 0 && (
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-400 flex items-center gap-1 mb-2"><Plane className="h-3 w-3" /> Flights</p>
              <div className="space-y-1.5">{trip.flights.map((fl) => (<div key={fl.id} className="text-sm"><span className="font-medium text-slate-800">{fl.label}</span><span className="text-slate-500"> · {fl.detail}</span></div>))}</div>
            </div>
          )}
          <div className="rounded-lg border border-slate-200 p-3"><p className="text-xs text-slate-400 flex items-center gap-1"><Building2 className="h-3 w-3" /> Hotel</p><p className="text-sm font-medium text-slate-800 mt-0.5">{trip.hotel || "—"}</p></div>
          <div className="rounded-lg border border-slate-200 p-3"><p className="text-xs text-slate-400 flex items-center gap-1"><Car className="h-3 w-3" /> Transfer</p><p className="text-sm font-medium text-slate-800 mt-0.5">{trip.transfer || "—"}</p></div>
          {trip.notes && <div className="rounded-lg border border-slate-200 p-3"><p className="text-xs text-slate-400">Notes</p><p className="text-sm text-slate-700 mt-0.5">{trip.notes}</p></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShareOpen(true)} className="gap-1.5 mr-auto" data-testid="trip-share-btn"><Share2 className="h-4 w-4" /> Share</Button>
          <Button variant="outline" onClick={() => { onOpenChange(false); onEdit(trip); }} className="gap-1.5" data-testid="trip-edit-btn"><Edit className="h-4 w-4" /> Edit</Button>
          <Button onClick={() => onOpenChange(false)} className="bg-neutral-600 hover:bg-neutral-700">Close</Button>
        </DialogFooter>
      </DialogContent>
      {shareOpen && <ShareDialog open={shareOpen} onOpenChange={setShareOpen} testidPrefix="trip-share"
        docTitle={`Itinerary — ${trip.destination}`} docSubtitle={trip.purpose} bodyHtml={shareHtml} shareUrl={shareUrl} />}
    </Dialog>
  );
};

export default function Travel() {
  const { trips, deleteTrip } = useFgp();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTrip, setEditTrip] = useState(null);
  const [detailTrip, setDetailTrip] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const openAdd = () => { setEditTrip(null); setDialogOpen(true); };
  const openEdit = (t) => { setEditTrip(t); setDialogOpen(true); };
  const openDetail = (t) => { setDetailTrip(t); setDetailOpen(true); };

  return (
    <div>
      <PageHeader title="Travel &amp; Itineraries" subtitle="Consolidated trips — flights, hotels, transfers and notes in one place."
        actions={<Button onClick={openAdd} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="add-trip"><Plus className="h-4 w-4" /> Add trip</Button>} />

      {trips.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <Plane className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700">No trips yet</p>
          <p className="text-xs text-slate-400 mt-1 mb-4">Plan flights, hotels and transfers for your principal.</p>
          <Button onClick={openAdd} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="empty-add-trip"><Plus className="h-4 w-4" /> Plan your first trip</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {trips.map((t, i) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              onClick={() => openDetail(t)} data-testid={`trip-card-${t.id}`}
              className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-slate-300 transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-lg bg-neutral-100 text-neutral-700 flex items-center justify-center flex-shrink-0"><Plane className="h-5 w-5" /></div>
                  <div><p className="font-heading font-semibold text-slate-900 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {t.destination}</p><p className="text-xs text-slate-400">{t.purpose}</p></div>
                </div>
                <StatusBadge status={t.status} />
              </div>
              <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><CalIcon className="h-3.5 w-3.5" /> {fmt(t.startDate)} → {fmt(t.endDate)}</span>
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {t.travelers || "—"}</span>
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                <span className="text-xs text-slate-400">{t.flights?.length || 0} flight(s) · {t.hotel ? "hotel booked" : "no hotel"}</span>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => openEdit(t)} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-neutral-600 hover:bg-slate-50" data-testid={`edit-trip-${t.id}`}><Edit className="h-4 w-4" /></button>
                  <button onClick={() => setToDelete(t)} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50" data-testid={`delete-trip-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <TripDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editTrip} />
      <TripDetail open={detailOpen} onOpenChange={setDetailOpen} trip={detailTrip} onEdit={openEdit} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-trip-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete this trip?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.destination}” will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-trip"
              onClick={() => { deleteTrip(toDelete.id); toast.success("Trip deleted"); setToDelete(null); }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
